import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { DirhamIconColored } from '../components/DirhamIcon';
import CurrencyDisplay, { CurrencyLabel } from '../components/CurrencyDisplay';
import { Textarea } from '../components/ui/textarea';
import {
  ViewersDialog,
  EvacuationDialog,
  BlockedBarbersDialog,
  AuthorizedBarbersDialog,
  HousingFormDialog,
} from '../components/housing/HousingDialogs';
import RoomsAndBedsManager from '../components/housing/RoomsAndBedsManager';
import { 
  Home, Plus, Trash2, Edit, Users, 
  TrendingUp, TrendingDown, Eye, UserPlus, AlertTriangle,
  CheckCircle, XCircle, Zap, Droplet, Wifi, Clock,
  FileText, Calendar, Award, LogOut, UserCheck, RefreshCw, Ban, ShieldOff,
  Percent, PauseCircle, MessageSquare, Send, Camera, Upload, Bell, DoorOpen
} from 'lucide-react';

const EmployeeHousing = () => {
  const { isAdmin, user } = useAuth();
  const [housings, setHousings] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHousing, setSelectedHousing] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [pendingResidents, setPendingResidents] = useState([]);
  const [pendingEvacuations, setPendingEvacuations] = useState([]);
  const [activeTab, setActiveTab] = useState('residents');
  const [pendingDeletions, setPendingDeletions] = useState([]);
  const [blockedBarbers, setBlockedBarbers] = useState([]);
  const [authorizedBarbers, setAuthorizedBarbers] = useState([]);
  const [pendingDiscounts, setPendingDiscounts] = useState([]);
  const [discountStats, setDiscountStats] = useState(null);
  
  // Dialogs
  const [showAddHousing, setShowAddHousing] = useState(false);
  const [showAddResident, setShowAddResident] = useState(false);
  const [showAddBarber, setShowAddBarber] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [showEvacuation, setShowEvacuation] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showBlockedBarbers, setShowBlockedBarbers] = useState(false);
  const [showAuthorizedBarbers, setShowAuthorizedBarbers] = useState(false);
  const [editingHousing, setEditingHousing] = useState(null);
  const [roomsBedsHousing, setRoomsBedsHousing] = useState(null);
  const [editingResident, setEditingResident] = useState(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedDiscountRequest, setSelectedDiscountRequest] = useState(null);
  const [discountAction, setDiscountAction] = useState(null); // 'approve', 'reject', 'modify', 'postpone'
  const [customDiscountPercent, setCustomDiscountPercent] = useState(50);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Forms
  const [housingForm, setHousingForm] = useState({
    name: '',
    annual_rent: 0,
    furniture_cost: 0,
    electricity_bill: 0,
    water_bill: 0,
    internet_bill: 0
  });

  const [residentForm, setResidentForm] = useState({
    user_id: '',
    name: '',
    nationality: 'غير محدد',
    phone: '',
    start_date: new Date().toISOString().split('T')[0],
    stay_cost: null,  // null = auto calculate (500 for barbers, 650 for external, bed price if bed selected)
    notes: '',
    permanent_stay: false,  // إقامة دائمة
    id_front_photo: null,  // صورة الهوية الأمامية
    id_back_photo: null,   // صورة الهوية الخلفية
    bed_id: ''             // Phase 3: linked bed
  });

  // طلب خصم
  const [discountRequest, setDiscountRequest] = useState({
    show: false,
    resident: null,
    reason: '',
    loading: false,
    conditionsMessage: null
  });

  // تنبيهات انتهاء الإقامة
  const [expiringResidents, setExpiringResidents] = useState([]);
  const [showExpiryAlert, setShowExpiryAlert] = useState(false);

  // Phase 3: available beds for the AddResident form
  const [availableBeds, setAvailableBeds] = useState([]);
  // Inline "quick add bed" from AddResident dialog (admin shortcut)
  const [housingRooms, setHousingRooms] = useState([]);
  const [showInlineBed, setShowInlineBed] = useState(false);
  const [inlineBed, setInlineBed] = useState({
    room_id: '', bed_number: '', bed_type: 'lower', price: '500', locker_number: ''
  });
  // Phase 5: pending renewal requests (admin queue)
  const [pendingRenewals, setPendingRenewals] = useState([]);

  // جلب الساكنين الذين تنتهي إقامتهم قريباً
  const fetchExpiringResidents = async () => {
    try {
      const res = await api.get('/housings/expiring-residents?days_before=7');
      // Transform response to match our expected format
      const transformed = (res.data.expiring_residents || []).map(r => ({
        ...r,
        days_until_expiry: r.days_remaining,
        warning_level: r.days_remaining <= 3 ? 'critical' : r.days_remaining <= 5 ? 'high' : 'medium'
      }));
      setExpiringResidents(transformed);
    } catch (error) {
      console.log('Failed to fetch expiring residents');
    }
  };

  // التأكيد على علم بانتهاء الإقامة
  const handleAcknowledgeExpiry = async (housingId, residentId, continueStay) => {
    try {
      await api.post(`/housings/${housingId}/residents/${residentId}/acknowledge-expiry`, {
        continue_stay: continueStay
      });
      toast.success('تم تسجيل العلم بتفاصيل انتهاء الفترة');
      fetchExpiringResidents();
      fetchHousings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    }
  };

  // حساب عدد الأيام من تاريخ البداية والنهاية
  const calculateDaysFromDates = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 ليشمل اليوم الأخير
    return diffDays > 0 ? diffDays : '';
  };

  // حساب تاريخ النهاية من تاريخ البداية وعدد الأيام
  const calculateEndDateFromDays = (startDate, days) => {
    if (!startDate || !days || days <= 0) return '';
    const start = new Date(startDate);
    start.setDate(start.getDate() + parseInt(days) - 1); // -1 لأن اليوم الأول يُحتسب
    return start.toISOString().split('T')[0];
  };

  // معالجة تغيير تاريخ النهاية
  const handleEndDateChange = (newEndDate) => {
    const daysCount = calculateDaysFromDates(residentForm.start_date, newEndDate);
    setResidentForm({ 
      ...residentForm, 
      end_date: newEndDate,
      days_count: daysCount
    });
  };

  // معالجة تغيير عدد الأيام
  const handleDaysCountChange = (newDaysCount) => {
    const endDate = calculateEndDateFromDays(residentForm.start_date, newDaysCount);
    setResidentForm({ 
      ...residentForm, 
      days_count: newDaysCount,
      end_date: endDate
    });
  };

  // معالجة تغيير تاريخ البداية (يُعيد حساب تاريخ النهاية إذا كان عدد الأيام محدداً)
  const handleStartDateChange = (newStartDate) => {
    let newEndDate = residentForm.end_date;
    let newDaysCount = residentForm.days_count;
    
    if (residentForm.days_count) {
      // إذا كان عدد الأيام محدداً، نحسب تاريخ النهاية الجديد
      newEndDate = calculateEndDateFromDays(newStartDate, residentForm.days_count);
    } else if (residentForm.end_date) {
      // إذا كان تاريخ النهاية محدداً، نحسب عدد الأيام الجديد
      newDaysCount = calculateDaysFromDates(newStartDate, residentForm.end_date);
    }
    
    setResidentForm({ 
      ...residentForm, 
      start_date: newStartDate,
      end_date: newEndDate,
      days_count: newDaysCount
    });
  };

  const [evacuationForm, setEvacuationForm] = useState({
    resident_id: '',
    evacuation_date: '',
    reason: ''
  });

  const [reportForm, setReportForm] = useState({
    report_type: 'monthly',
    month: new Date().toISOString().slice(0, 7),
    start_date: '',
    end_date: ''
  });

  const [reportData, setReportData] = useState(null);

  // Fetch data
  const fetchHousings = useCallback(async () => {
    try {
      const res = await api.get('/housings');
      setHousings(res.data);
      if (res.data.length > 0 && !selectedHousing) {
        setSelectedHousing(res.data[0]);
        fetchProfitability(res.data[0].id);
        if (isAdmin()) {
          fetchPendingResidents(res.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch housings:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedHousing, isAdmin]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setAllUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchAvailableUsers = async (housingId) => {
    try {
      const res = await api.get(`/housings/available-users?housing_id=${housingId}`);
      setAvailableUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch available users');
    }
  };

  const fetchProfitability = async (housingId) => {
    try {
      const res = await api.get(`/housings/${housingId}/profitability`);
      setProfitability(res.data);
    } catch (error) {
      console.error('Failed to fetch profitability');
      setProfitability(null);
    }
  };

  const fetchPendingResidents = async (housingId) => {
    try {
      const res = await api.get(`/housings/${housingId}/residents/pending`);
      setPendingResidents(res.data);
    } catch (error) {
      console.error('Failed to fetch pending residents');
    }
  };

  const fetchPendingEvacuations = async () => {
    try {
      const res = await api.get('/housings/evacuation-requests/pending');
      setPendingEvacuations(res.data);
    } catch (error) {
      console.error('Failed to fetch pending evacuations');
    }
  };

  const fetchPendingDeletions = async () => {
    try {
      const res = await api.get('/housings/deletion-requests/pending');
      setPendingDeletions(res.data);
    } catch (error) {
      console.error('Failed to fetch pending deletions');
    }
  };

  const fetchPendingDiscounts = async () => {
    try {
      const res = await api.get('/housings/discount-requests/pending');
      // Handle both array and object responses
      const discounts = Array.isArray(res.data) ? res.data : (res.data.requests || []);
      setPendingDiscounts(discounts);
    } catch (error) {
      console.error('Failed to fetch pending discounts');
      setPendingDiscounts([]);
    }
  };

  const fetchDiscountStats = async () => {
    try {
      const res = await api.get('/housings/discount-requests/stats');
      setDiscountStats(res.data);
    } catch (error) {
      console.error('Failed to fetch discount stats');
    }
  };

  const checkDiscountEligibility = async (housingId) => {
    try {
      const res = await api.get(`/housings/${housingId}/check-discount-eligibility`);
      if (res.data.created_requests > 0) {
        toast.success(`تم إنشاء ${res.data.created_requests} طلب خصم جديد`);
        fetchPendingDiscounts();
      } else if (res.data.eligible_residents.length > 0) {
        toast.info(`${res.data.eligible_residents.length} ساكن مؤهل للخصم`);
      } else {
        toast.info('لا يوجد ساكنون مؤهلون للخصم حالياً');
      }
      return res.data;
    } catch (error) {
      toast.error('فشل فحص أهلية الخصم');
      return null;
    }
  };

  useEffect(() => {
    fetchHousings();
    if (isAdmin()) {
      fetchUsers();
      fetchPendingEvacuations();
      fetchPendingDeletions();
      fetchPendingDiscounts();
      fetchDiscountStats();
      fetchExpiringResidents();
      fetchPendingRenewals();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedHousing && isAdmin()) {
      fetchAvailableUsers(selectedHousing.id);
      fetchPendingResidents(selectedHousing.id);
      fetchBlockedBarbers(selectedHousing.id);
      fetchAuthorizedBarbers(selectedHousing.id);
    }
    if (selectedHousing) {
      fetchAvailableBeds(selectedHousing.id);
    }
  }, [selectedHousing]);

  const fetchBlockedBarbers = async (housingId) => {
    try {
      const res = await api.get(`/housings/${housingId}/blocked-barbers`);
      setBlockedBarbers(res.data);
    } catch (error) {
      console.error('Failed to fetch blocked barbers');
    }
  };

  const fetchAuthorizedBarbers = async (housingId) => {
    try {
      const res = await api.get(`/housings/${housingId}/authorized-barbers`);
      setAuthorizedBarbers(res.data);
    } catch (error) {
      console.error('Failed to fetch authorized barbers');
    }
  };

  // Phase 3: fetch available beds for the selected housing (used by AddResident form)
  const fetchAvailableBeds = async (housingId) => {
    try {
      const res = await api.get(`/housings/${housingId}/available-beds`);
      setAvailableBeds(res.data || []);
    } catch (error) {
      setAvailableBeds([]);
    }
  };

  // Inline "quick add bed" from AddResident dialog
  const fetchHousingRooms = async (housingId) => {
    try {
      const res = await api.get(`/housings/${housingId}/rooms`);
      setHousingRooms(res.data || []);
    } catch (error) {
      setHousingRooms([]);
    }
  };

  const handleInlineAddBed = async () => {
    if (!selectedHousing) return;
    const { room_id, bed_number, bed_type, price, locker_number } = inlineBed;
    if (!room_id) { toast.error('اختر الغرفة أولاً'); return; }
    if (!bed_number.trim()) { toast.error('رقم السرير مطلوب'); return; }
    if (!locker_number.trim()) { toast.error('رقم الخزانة مطلوب'); return; }
    if (!price || Number(price) <= 0) { toast.error('أدخل سعراً صحيحاً'); return; }
    try {
      const res = await api.post(
        `/housings/${selectedHousing.id}/rooms/${room_id}/beds`,
        {
          bed_number: bed_number.trim(),
          bed_type,
          price: Number(price),
          locker_number: locker_number.trim(),
        }
      );
      toast.success('تمت إضافة السرير — تم تحديده تلقائياً');
      const newBedId = res.data?.id;
      // Refresh available beds and auto-select the newly created bed
      await fetchAvailableBeds(selectedHousing.id);
      if (newBedId) {
        setResidentForm((prev) => ({ ...prev, bed_id: newBedId, stay_cost: Number(price) }));
      }
      setShowInlineBed(false);
      setInlineBed({ room_id, bed_number: '', bed_type: 'lower', price: '500', locker_number: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل إضافة السرير');
    }
  };

  // Phase 5: fetch pending renewal requests (admin)
  const fetchPendingRenewals = async () => {
    try {
      const res = await api.get('/housings/renewal-requests/pending');
      setPendingRenewals(res.data || []);
    } catch (error) {
      setPendingRenewals([]);
    }
  };

  const handleSelectHousing = (housing) => {
    setSelectedHousing(housing);
    fetchProfitability(housing.id);
    if (isAdmin()) {
      fetchPendingResidents(housing.id);
      fetchAvailableUsers(housing.id);
      fetchBlockedBarbers(housing.id);
      fetchAuthorizedBarbers(housing.id);
    }
  };

  // Housing CRUD
  const handleAddHousing = async (e) => {
    e.preventDefault();
    try {
      await api.post('/housings', housingForm);
      toast.success('تم إضافة السكن بنجاح');
      setShowAddHousing(false);
      setHousingForm({
        name: '', annual_rent: 0, furniture_cost: 0,
        electricity_bill: 0, water_bill: 0, internet_bill: 0
      });
      fetchHousings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة السكن');
    }
  };

  const handleUpdateHousing = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/housings/${editingHousing.id}`, housingForm);
      toast.success('تم تحديث السكن بنجاح');
      setEditingHousing(null);
      fetchHousings();
      if (selectedHousing?.id === editingHousing.id) {
        fetchProfitability(editingHousing.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث السكن');
    }
  };

  const handleDeleteHousing = async (housingId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا السكن وجميع بياناته؟')) return;
    try {
      await api.delete(`/housings/${housingId}`);
      toast.success('تم حذف السكن بنجاح');
      if (selectedHousing?.id === housingId) {
        setSelectedHousing(null);
        setProfitability(null);
      }
      fetchHousings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حذف السكن');
    }
  };

  // Resident CRUD
  const handleAddResident = async (e) => {
    e.preventDefault();
    try {
      const data = { ...residentForm };
      if (!data.user_id) delete data.user_id;
      // Remove file objects and preview URLs
      delete data.id_front_file;
      delete data.id_back_file;
      delete data.id_front_preview;
      delete data.id_back_preview;
      // Remove null/undefined values
      Object.keys(data).forEach(key => {
        if (data[key] === null || data[key] === undefined || data[key] === '') {
          delete data[key];
        }
      });
      
      const res = await api.post(`/housings/${selectedHousing.id}/residents`, data);
      const newResidentId = res.data.resident?.id;
      
      // Upload ID photos if provided
      if (newResidentId) {
        if (residentForm.id_front_file) {
          const formData = new FormData();
          formData.append('file', residentForm.id_front_file);
          try {
            await api.post(
              `/housings/${selectedHousing.id}/residents/${newResidentId}/upload-id-photo?photo_type=front`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
          } catch (err) {
            console.log('Failed to upload front ID photo');
          }
        }
        if (residentForm.id_back_file) {
          const formData = new FormData();
          formData.append('file', residentForm.id_back_file);
          try {
            await api.post(
              `/housings/${selectedHousing.id}/residents/${newResidentId}/upload-id-photo?photo_type=back`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
          } catch (err) {
            console.log('Failed to upload back ID photo');
          }
        }
      }
      
      toast.success(res.data.message);
      setShowAddResident(false);
      setResidentForm({
        user_id: '', name: '', nationality: 'غير محدد', phone: '',
        start_date: new Date().toISOString().split('T')[0],
        stay_cost: null, notes: '', permanent_stay: false,
        id_front_photo: null, id_back_photo: null,
        id_front_file: null, id_back_file: null,
        id_front_preview: null, id_back_preview: null,
        bed_id: ''
      });
      fetchHousings();
      fetchProfitability(selectedHousing.id);
      fetchAvailableBeds(selectedHousing.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة الساكن');
    }
  };

  // Phase 5: request or apply a renewal for a resident
  const handleRenewResident = async (residentId, residentName) => {
    if (!selectedHousing) return;
    const admin = isAdmin();
    const label = admin ? 'تجديد إقامة' : 'طلب تجديد إقامة';
    if (!window.confirm(`${label} ${residentName || ''} لمدة شهر إضافي؟`)) return;
    try {
      const res = await api.post(
        `/housings/${selectedHousing.id}/residents/${residentId}/renew`,
        { months: 1 }
      );
      toast.success(res.data.message);
      fetchHousings();
      fetchExpiringResidents();
      if (admin) fetchPendingRenewals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل التجديد');
    }
  };

  // Phase 5: admin approves or rejects a pending renewal
  const handleApproveRenewal = async (housingId, residentId, approved) => {
    try {
      await api.post(
        `/housings/${housingId}/residents/${residentId}/renew/approve?approved=${approved}`
      );
      toast.success(approved ? 'تمت الموافقة على التجديد' : 'تم رفض طلب التجديد');
      fetchPendingRenewals();
      fetchHousings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    }
  };

  const handleAddBarberResidents = async (userIds) => {
    try {
      await api.post(`/housings/${selectedHousing.id}/add-barber-residents`, userIds);
      toast.success('تم إضافة الحلاقين كساكنين');
      setShowAddBarber(false);
      fetchHousings();
      fetchProfitability(selectedHousing.id);
      fetchAvailableUsers(selectedHousing.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة الحلاقين');
    }
  };

  const handleUpdateResident = async (residentId, updateData) => {
    try {
      await api.put(`/housings/${selectedHousing.id}/residents/${residentId}`, updateData);
      toast.success('تم تحديث بيانات الساكن');
      setEditingResident(null);
      fetchHousings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث الساكن');
    }
  };

  const handleAdminUpdateResident = async (residentId, customRent, endDate = null, permanentStay = null) => {
    try {
      let url = `/housings/${selectedHousing.id}/residents/${residentId}/admin?`;
      const params = [];
      if (customRent !== null && customRent !== undefined) params.push(`custom_rent=${customRent}`);
      if (endDate !== null) params.push(`end_date=${endDate}`);
      if (permanentStay !== null) params.push(`permanent_stay=${permanentStay}`);
      url += params.join('&');
      
      await api.put(url);
      toast.success('تم تحديث بيانات الساكن');
      fetchHousings();
      fetchProfitability(selectedHousing.id);
      if (isAdmin()) fetchExpiringResidents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل التحديث');
    }
  };

  const handleDeleteResident = async (residentId) => {
    if (!window.confirm('هل أنت متأكد من إزالة هذا الساكن؟')) return;
    try {
      const res = await api.delete(`/housings/${selectedHousing.id}/residents/${residentId}`);
      toast.success(res.data.message || 'تم إزالة الساكن بنجاح');
      fetchHousings();
      fetchProfitability(selectedHousing.id);
      if (isAdmin()) fetchPendingDeletions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إزالة الساكن');
    }
  };

  const handleApproveResident = async (residentId, approved) => {
    try {
      await api.post(`/housings/${selectedHousing.id}/residents/${residentId}/approve?approved=${approved}`);
      toast.success(approved ? 'تم الموافقة على الساكن' : 'تم رفض الساكن');
      fetchHousings();
      fetchPendingResidents(selectedHousing.id);
      fetchProfitability(selectedHousing.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    }
  };

  // طلب خصم للساكن
  const handleRequestDiscount = async (resident) => {
    // عرض نافذة طلب الخصم
    setDiscountRequest({
      show: true,
      resident: resident,
      reason: '',
      loading: false,
      conditionsMessage: resident.is_system_user ? 
        `شروط الخصم للحلاقين:\n━━━━━━━━━━━━━━━━━━━━\n1️⃣ إيرادات الشهر ≥ 5,000 درهم\n2️⃣ عدد السكان في الوحدة ≥ 8 ساكنين\n━━━━━━━━━━━━━━━━━━━━\nإذا تحققت الشروط يحصل على خصم 50%` 
        : null
    });
  };

  // المدير يطبق خصم 100 درهم لشهر واحد على ساكن غير حلاق
  const handleApplySimpleDiscount = async (housingId, resident) => {
    const defaultMonth = (resident.start_date || '').substring(0, 7);
    const month = window.prompt(
      `تطبيق خصم 100 درهم على ${resident.name} (650 → 550)\nاكتب الشهر بصيغة YYYY-MM (افتراضي: شهر دخوله ${defaultMonth || 'الحالي'}):`,
      defaultMonth || ''
    );
    if (month === null) return; // user cancelled
    try {
      const res = await api.post(
        `/housings/${housingId}/residents/${resident.id}/apply-monthly-discount`,
        { month: month.trim() || null, amount: 100, note: 'بأمر المدير' }
      );
      toast.success(res.data.message);
      fetchHousings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تطبيق الخصم');
    }
  };

  // المدير يلغي خصم شهر معين
  const handleRemoveSimpleDiscount = async (housingId, resident) => {
    const month = resident.current_month;
    if (!window.confirm(`إلغاء خصم ${resident.current_month_discount} درهم لشهر ${month} للساكن ${resident.name}؟`)) return;
    try {
      const res = await api.delete(
        `/housings/${housingId}/residents/${resident.id}/monthly-discount/${month}`
      );
      toast.success(res.data.message);
      fetchHousings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل إلغاء الخصم');
    }
  };

  // إرسال طلب الخصم
  const submitDiscountRequest = async () => {
    if (!discountRequest.resident) return;
    
    setDiscountRequest(prev => ({ ...prev, loading: true }));
    
    try {
      const res = await api.post(
        `/housings/${selectedHousing.id}/residents/${discountRequest.resident.id}/request-discount`,
        {
          resident_id: discountRequest.resident.id,
          reason: discountRequest.reason,
          requested_percentage: 50
        }
      );
      
      toast.success(res.data.message);
      
      // إذا كان حلاقاً، عرض رسالة الشروط
      if (res.data.conditions_message) {
        toast.info(res.data.conditions_message, { duration: 10000 });
      }
      
      setDiscountRequest({ show: false, resident: null, reason: '', loading: false, conditionsMessage: null });
      fetchHousings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال طلب الخصم');
      setDiscountRequest(prev => ({ ...prev, loading: false }));
    }
  };

  // Evacuation
  const handleCreateEvacuation = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/housings/${selectedHousing.id}/evacuation-requests`, evacuationForm);
      toast.success('تم إرسال طلب الإخلاء للموافقة');
      setShowEvacuation(false);
      setEvacuationForm({ resident_id: '', evacuation_date: '', reason: '' });
      if (isAdmin()) fetchPendingEvacuations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال طلب الإخلاء');
    }
  };

  const handleApproveEvacuation = async (requestId, approved) => {
    try {
      await api.post(`/housings/evacuation-requests/${requestId}/approve?approved=${approved}`);
      toast.success(approved ? 'تم الموافقة على طلب الإخلاء' : 'تم رفض طلب الإخلاء');
      fetchPendingEvacuations();
      fetchHousings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    }
  };

  const handleApproveDeletion = async (requestId, approved) => {
    try {
      await api.post(`/housings/deletion-requests/${requestId}/approve?approved=${approved}`);
      toast.success(approved ? 'تم الموافقة على طلب الحذف' : 'تم رفض طلب الحذف');
      fetchPendingDeletions();
      fetchHousings();
      if (selectedHousing) fetchProfitability(selectedHousing.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    }
  };

  // Discount Request Handlers
  const openDiscountModal = (request, action) => {
    setSelectedDiscountRequest(request);
    setDiscountAction(action);
    setCustomDiscountPercent(request.requested_discount_percentage || 50);
    setRejectionReason('');
    setShowDiscountModal(true);
  };

  const handleDiscountAction = async () => {
    if (!selectedDiscountRequest) return;
    
    try {
      switch (discountAction) {
        case 'approve':
          await api.post(`/housings/discount-requests/${selectedDiscountRequest.id}/approve`);
          toast.success('تمت الموافقة على طلب الخصم');
          break;
        case 'modify':
          await api.post(`/housings/discount-requests/${selectedDiscountRequest.id}/modify?new_percentage=${customDiscountPercent}`);
          toast.success(`تم تعديل نسبة الخصم إلى ${customDiscountPercent}%`);
          break;
        case 'reject':
          if (!rejectionReason.trim()) {
            toast.error('يرجى كتابة سبب الرفض');
            return;
          }
          await api.post(`/housings/discount-requests/${selectedDiscountRequest.id}/reject?reason=${encodeURIComponent(rejectionReason)}`);
          toast.success('تم رفض طلب الخصم');
          break;
        case 'postpone':
          await api.post(`/housings/discount-requests/${selectedDiscountRequest.id}/postpone`);
          toast.success('تم تأجيل القرار على طلب الخصم');
          break;
        default:
          break;
      }
      
      setShowDiscountModal(false);
      setSelectedDiscountRequest(null);
      setDiscountAction(null);
      fetchPendingDiscounts();
      fetchDiscountStats();
      fetchHousings();
      if (selectedHousing) fetchProfitability(selectedHousing.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    }
  };

  // Viewers
  const handleAddViewer = async (userId) => {
    try {
      await api.post(`/housings/${selectedHousing.id}/viewers?user_id=${userId}`);
      toast.success('تم إضافة المشاهد بنجاح');
      fetchHousings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة المشاهد');
    }
  };

  const handleRemoveViewer = async (userId) => {
    try {
      await api.delete(`/housings/${selectedHousing.id}/viewers/${userId}`);
      toast.success('تم إزالة المشاهد');
      fetchHousings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إزالة المشاهد');
    }
  };

  // Block/Unblock Barbers
  const handleBlockBarber = async (userId) => {
    try {
      await api.post(`/housings/${selectedHousing.id}/blocked-barbers?user_id=${userId}`);
      toast.success('تم حجب الحلاق من إضافة الساكنين');
      fetchBlockedBarbers(selectedHousing.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حجب الحلاق');
    }
  };

  const handleUnblockBarber = async (userId) => {
    try {
      await api.delete(`/housings/${selectedHousing.id}/blocked-barbers/${userId}`);
      toast.success('تم إلغاء حجب الحلاق');
      fetchBlockedBarbers(selectedHousing.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إلغاء الحجب');
    }
  };

  // Authorize/Revoke Barbers
  const handleAuthorizeBarber = async (userId) => {
    try {
      await api.post(`/housings/${selectedHousing.id}/authorized-barbers?user_id=${userId}`);
      toast.success('تم تصريح المستخدم لإضافة الساكنين');
      fetchAuthorizedBarbers(selectedHousing.id);
      fetchBlockedBarbers(selectedHousing.id); // Refresh blocked list too
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل التصريح');
    }
  };

  const handleRevokeAuthorization = async (userId) => {
    try {
      await api.delete(`/housings/${selectedHousing.id}/authorized-barbers/${userId}`);
      toast.success('تم إلغاء تصريح المستخدم');
      fetchAuthorizedBarbers(selectedHousing.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إلغاء التصريح');
    }
  };

  // Reports
  const handleGenerateReport = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post(`/housings/${selectedHousing.id}/reports`, reportForm);
      setReportData(res.data);
      toast.success('تم إنشاء التقرير');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إنشاء التقرير');
    }
  };

  const startEditHousing = (housing) => {
    setHousingForm({
      name: housing.name,
      annual_rent: housing.annual_rent,
      furniture_cost: housing.furniture_cost,
      electricity_bill: housing.electricity_bill,
      water_bill: housing.water_bill,
      internet_bill: housing.internet_bill
    });
    setEditingHousing(housing);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const canEdit = isAdmin();
  const monthlyRent = selectedHousing ? selectedHousing.annual_rent / 12 : 0;
  const activeResidents = selectedHousing?.residents?.filter(r => r.is_active && r.status === 'approved') || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Home className="w-8 h-8 text-primary" />
            سكن الموظفين
          </h1>
          <p className="text-muted-foreground">إدارة سكن الموظفين وتتبع الإيرادات والمصاريف</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEdit && (
            <>
              <Button
                data-testid="add-housing-btn"
                onClick={() => setShowAddHousing(true)}
                className="bg-primary text-primary-foreground"
              >
                <Plus className="w-4 h-4 me-2" />
                إضافة سكن
              </Button>
              <HousingFormDialog
                open={showAddHousing}
                onOpenChange={setShowAddHousing}
                mode="add"
                form={housingForm}
                setForm={setHousingForm}
                onSubmit={handleAddHousing}
              />
            </>
          )}
        </div>
      </div>

      {housings.length === 0 ? (
        <div className="bg-card border border-white/5 rounded-xl p-12 text-center">
          <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">لا يوجد سكن مسجل</h2>
          <p className="text-muted-foreground mb-4">ابدأ بإضافة سكن جديد للموظفين</p>
          {canEdit && (
            <Button onClick={() => setShowAddHousing(true)} className="bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 me-2" />
              إضافة سكن جديد
            </Button>
          )}
        </div>
      ) : (
        <>
        {/* تنبيهات انتهاء الإقامة */}
        {expiringResidents.length > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-orange-400 flex items-center gap-2">
                <Bell className="w-5 h-5" />
                تنبيهات انتهاء الإقامة ({expiringResidents.length})
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowExpiryAlert(!showExpiryAlert)}
                className="text-orange-400"
              >
                {showExpiryAlert ? 'إخفاء' : 'عرض الكل'}
              </Button>
            </div>
            
            {/* الساكنين الأكثر إلحاحاً (3 أيام أو أقل) */}
            {expiringResidents.filter(r => r.days_until_expiry <= 3).length > 0 && (
              <div className="space-y-2 mb-3">
                {expiringResidents.filter(r => r.days_until_expiry <= 3).map(resident => (
                  <div key={resident.resident_id} className="flex items-center justify-between p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                        resident.days_until_expiry === 0 ? 'bg-red-600' : 'bg-red-500'
                      }`}>
                        {resident.days_until_expiry === 0 ? 'اليوم!' : resident.days_until_expiry}
                      </div>
                      <div>
                        <p className="font-medium">{resident.resident_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {resident.housing_name} - ينتهي: {resident.end_date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {resident.barber_acknowledged ? (
                        <Badge className="bg-green-500/20 text-green-400 text-xs">✓ الحلاقين على علم</Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAcknowledgeExpiry(resident.housing_id, resident.resident_id, true)}
                          className="text-xs border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
                        >
                          على علم
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* باقي الساكنين (إذا كان showExpiryAlert مفعلاً) */}
            {showExpiryAlert && expiringResidents.filter(r => r.days_until_expiry > 3).length > 0 && (
              <div className="space-y-2 mt-3 pt-3 border-t border-white/10">
                <p className="text-sm text-muted-foreground mb-2">ساكنين آخرين (4-7 أيام):</p>
                {expiringResidents.filter(r => r.days_until_expiry > 3).map(resident => (
                  <div key={resident.resident_id} className="flex items-center justify-between p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-yellow-500/30 flex items-center justify-center text-xs font-bold">
                        {resident.days_until_expiry}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{resident.resident_name}</p>
                        <p className="text-xs text-muted-foreground">{resident.housing_name}</p>
                      </div>
                    </div>
                    {resident.barber_acknowledged ? (
                      <Badge className="bg-green-500/20 text-green-400 text-xs">✓ على علم</Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAcknowledgeExpiry(resident.housing_id, resident.resident_id, true)}
                        className="text-xs text-yellow-400"
                      >
                        على علم
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Housing List Sidebar */}
          <div className="lg:col-span-1 space-y-3">
            <h3 className="font-bold text-lg mb-3">قائمة السكنات</h3>
            {housings.map((housing) => (
              <div
                key={housing.id}
                onClick={() => handleSelectHousing(housing)}
                className={`p-4 rounded-xl cursor-pointer transition-all ${
                  selectedHousing?.id === housing.id
                    ? 'bg-primary/20 border border-primary/50'
                    : 'bg-card border border-white/5 hover:border-white/20'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-primary" />
                    <span className="font-medium">{housing.name}</span>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1">
                      <Button
                        onClick={(e) => { e.stopPropagation(); setRoomsBedsHousing(housing); }}
                        variant="ghost"
                        size="sm"
                        className="p-1 h-auto text-amber-500 hover:text-amber-400"
                        title="إدارة الغرف والأسرّة"
                        data-testid={`rooms-beds-btn-${housing.id}`}
                      >
                        <DoorOpen className="w-3 h-3" />
                      </Button>
                      <Button
                        onClick={(e) => { e.stopPropagation(); startEditHousing(housing); }}
                        variant="ghost"
                        size="sm"
                        className="p-1 h-auto"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleDeleteHousing(housing.id); }}
                        variant="ghost"
                        size="sm"
                        className="p-1 h-auto text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {housing.residents?.filter(r => r.is_active && r.status === 'approved').length || 0} ساكن
                </div>
              </div>
            ))}
          </div>

          {/* Main Content */}
          {selectedHousing && (
            <div className="lg:col-span-3 space-y-6">
              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="bg-secondary/50 mb-4">
                  <TabsTrigger value="residents" className="data-[state=active]:bg-primary">
                    <Users className="w-4 h-4 me-2" />
                    السكان
                  </TabsTrigger>
                  <TabsTrigger value="stats" className="data-[state=active]:bg-primary">
                    <TrendingUp className="w-4 h-4 me-2" />
                    الإحصائيات
                  </TabsTrigger>
                  {canEdit && (
                    <>
                      <TabsTrigger value="pending" className="data-[state=active]:bg-primary">
                        <Clock className="w-4 h-4 me-2" />
                        المعلق
                        {(pendingResidents.length + pendingEvacuations.length + pendingDeletions.length + pendingDiscounts.length) > 0 && (
                          <Badge className="ms-2 bg-yellow-500">{pendingResidents.length + pendingEvacuations.length + pendingDeletions.length + pendingDiscounts.length}</Badge>
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="reports" className="data-[state=active]:bg-primary">
                        <FileText className="w-4 h-4 me-2" />
                        التقارير
                      </TabsTrigger>
                    </>
                  )}
                </TabsList>

                {/* Residents Tab */}
                <TabsContent value="residents" className="space-y-4">
                  {/* Expiring Residents Alert */}
                  {isAdmin() && expiringResidents.length > 0 && (
                    <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                      <div className="flex items-center gap-3 mb-3">
                        <AlertTriangle className="w-5 h-5 text-orange-500" />
                        <h4 className="font-bold text-orange-500">
                          تنبيه: {expiringResidents.length} ساكن تنتهي إقامتهم قريباً
                        </h4>
                      </div>
                      <div className="space-y-2">
                        {expiringResidents.slice(0, 5).map((resident) => (
                          <div key={resident.resident_id || `${resident.housing_id}-${resident.resident_name}`} className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg text-sm">
                            <div className="flex items-center gap-2">
                              {resident.is_barber && (
                                <Badge variant="outline" className="text-xs bg-blue-500/20 text-blue-400 border-blue-500/30">
                                  حلاق
                                </Badge>
                              )}
                              <span>{resident.resident_name}</span>
                              <span className="text-muted-foreground">- {resident.housing_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`font-bold ${
                                resident.days_remaining === 0 ? 'text-red-500' : 
                                resident.days_remaining === 1 ? 'text-orange-500' : 
                                'text-yellow-500'
                              }`}>
                                {resident.days_remaining === 0 ? 'اليوم!' : 
                                 resident.days_remaining === 1 ? 'غداً' : 
                                 `${resident.days_remaining} أيام`}
                              </span>
                              <span className="text-muted-foreground text-xs">({resident.end_date})</span>
                            </div>
                          </div>
                        ))}
                        {expiringResidents.length > 5 && (
                          <p className="text-sm text-muted-foreground text-center">
                            و {expiringResidents.length - 5} آخرين...
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {isAdmin() && (
                      <Button
                        size="sm"
                        onClick={() => setRoomsBedsHousing(selectedHousing)}
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                        data-testid="manage-rooms-beds-btn"
                        title="إعداد الغرف والأسِرَّة والخزائن"
                      >
                        <DoorOpen className="w-4 h-4 me-1" />
                        إدارة الغرف والأسِرَّة
                      </Button>
                    )}
                    {canEdit && (
                      <>
                        <Dialog open={showAddBarber} onOpenChange={setShowAddBarber}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700">
                              <UserCheck className="w-4 h-4 me-1" />
                              إضافة حلاقين
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-card border-white/10">
                            <DialogHeader>
                              <DialogTitle>إضافة حلاقين كساكنين</DialogTitle>
                              <DialogDescription>اختر الحلاقين لإضافتهم تلقائياً بـ 500 درهم/شهر</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3 max-h-60 overflow-y-auto">
                              {availableUsers.filter(u => u.role === 'barber').map(user => (
                                <div key={user.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                                  <span>{user.name}</span>
                                  <Button
                                    size="sm"
                                    onClick={() => handleAddBarberResidents([user.id])}
                                    className="bg-primary"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                              {availableUsers.filter(u => u.role === 'barber').length === 0 && (
                                <p className="text-center text-muted-foreground py-4">جميع الحلاقين مضافين بالفعل</p>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                    
                    <Dialog open={showAddResident} onOpenChange={(open) => {
                      setShowAddResident(open);
                      if (open && selectedHousing) {
                        fetchAvailableBeds(selectedHousing.id);
                        if (isAdmin()) fetchHousingRooms(selectedHousing.id);
                      }
                      if (!open) setShowInlineBed(false);
                    }}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-primary text-primary-foreground" data-testid="add-resident-btn">
                          <UserPlus className="w-4 h-4 me-1" />
                          إضافة ساكن
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-card border-white/10 max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>إضافة ساكن جديد</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddResident} className="space-y-4">
                          {canEdit && (
                            <div>
                              <label className="block text-sm font-medium mb-2">ربط بمستخدم (اختياري)</label>
                              <Select 
                                value={residentForm.user_id || "manual"} 
                                onValueChange={(v) => {
                                  if (v === "manual") {
                                    setResidentForm({ 
                                      ...residentForm, 
                                      user_id: "",
                                      name: ""
                                    });
                                  } else {
                                    const selectedUser = availableUsers.find(u => u.id === v);
                                    setResidentForm({ 
                                      ...residentForm, 
                                      user_id: v,
                                      name: selectedUser?.name || residentForm.name
                                    });
                                  }
                                }}
                              >
                                <SelectTrigger className="bg-secondary/50 border-white/10">
                                  <SelectValue placeholder="اختر مستخدم أو أدخل اسم يدوياً" />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-white/10">
                                  <SelectItem value="manual">إدخال يدوي</SelectItem>
                                  {availableUsers.map(user => (
                                    <SelectItem key={user.id} value={user.id}>
                                      {user.name} ({user.role === 'barber' ? 'حلاق' : 'مستخدم'})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium mb-2">الاسم</label>
                            <Input
                              value={residentForm.name}
                              onChange={(e) => setResidentForm({ ...residentForm, name: e.target.value })}
                              required={!residentForm.user_id}
                              disabled={!!residentForm.user_id}
                              className="bg-secondary/50 border-white/10"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium mb-2">الجنسية</label>
                              <Input
                                value={residentForm.nationality}
                                onChange={(e) => setResidentForm({ ...residentForm, nationality: e.target.value })}
                                className="bg-secondary/50 border-white/10"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">الهاتف</label>
                              <Input
                                value={residentForm.phone}
                                onChange={(e) => setResidentForm({ ...residentForm, phone: e.target.value })}
                                className="bg-secondary/50 border-white/10"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium mb-2">تاريخ البداية</label>
                              <Input
                                type="date"
                                value={residentForm.start_date}
                                onChange={(e) => setResidentForm({ ...residentForm, start_date: e.target.value })}
                                required
                                className="bg-secondary/50 border-white/10"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">
                                الإيجار الشهري 
                                <span className="text-xs text-muted-foreground ms-1">
                                  (فارغ = {residentForm.user_id ? '500' : '650'} تلقائي)
                                </span>
                              </label>
                              <Input
                                type="number"
                                value={residentForm.stay_cost || ''}
                                onChange={(e) => setResidentForm({ ...residentForm, stay_cost: e.target.value ? parseFloat(e.target.value) : null })}
                                placeholder={residentForm.user_id ? '500 للحلاقين' : '650 للخارجيين'}
                                className="bg-secondary/50 border-white/10"
                              />
                            </div>
                          </div>

                          {/* Phase 3: bed selection */}
                          <div>
                            <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                              <DoorOpen className="w-4 h-4 text-primary" />
                              اختر سريراً متاحاً <span className="text-red-400">*</span>
                              <span className="text-xs text-muted-foreground">(إلزامي — يعبّئ السعر ورقم الخزانة تلقائياً)</span>
                            </label>
                            {availableBeds.length === 0 && (
                              <div className="mb-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-300" data-testid="no-beds-hint">
                                <div className="font-semibold mb-1">يجب على المدير إعداد الغرف والأسِرَّة أولاً</div>
                                <ul className="list-disc list-inside space-y-0.5">
                                  <li>إضافة الغرف مع أسمائها وعدد الأسِرَّة</li>
                                  <li>تحديد نوع كل سرير (علوي / سفلي)</li>
                                  <li>تحديد سعر كل سرير ورقم الخزانة</li>
                                </ul>
                                {isAdmin() && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => {
                                      setShowAddResident(false);
                                      setRoomsBedsHousing(selectedHousing);
                                    }}
                                    className="mt-2 bg-amber-500 hover:bg-amber-600 text-black text-xs"
                                    data-testid="open-rooms-manager-from-hint"
                                  >
                                    <DoorOpen className="w-3.5 h-3.5 me-1" />
                                    افتح «إدارة الغرف والأسِرَّة» الآن
                                  </Button>
                                )}
                                {!isAdmin() && (
                                  <div className="mt-2">اطلب من المدير فتح «إدارة الغرف والأسِرَّة» من صفحة السكن.</div>
                                )}
                              </div>
                            )}
                            <Select
                              value={residentForm.bed_id || 'none'}
                              onValueChange={(v) => {
                                if (v === 'none') {
                                  setResidentForm({ ...residentForm, bed_id: '' });
                                  return;
                                }
                                if (!v) return; // ignore Radix's transient empty callback while a new SelectItem is mounting
                                const bed = availableBeds.find((b) => b.id === v);
                                setResidentForm({
                                  ...residentForm,
                                  bed_id: v,
                                  // Auto-fill price from the bed unless user already set a custom one
                                  stay_cost: bed ? bed.price : residentForm.stay_cost,
                                });
                              }}
                            >
                              <SelectTrigger data-testid="select-bed-trigger" className="bg-secondary/50 border-white/10">
                                <SelectValue placeholder={availableBeds.length ? 'اختر سريراً…' : 'لا يوجد أسرّة متاحة'} />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-white/10">
                                <SelectItem value="none">بدون تحديد سرير</SelectItem>
                                {availableBeds.map((bed) => (
                                  <SelectItem key={bed.id} value={bed.id} data-testid={`bed-option-${bed.bed_number}`}>
                                    سرير {bed.bed_number} — {bed.room_name || 'غرفة'} ({bed.bed_type === 'upper' ? 'علوي' : 'سفلي'}) · {bed.price} د.إ
                                    {bed.locker_number ? ` · خزانة ${bed.locker_number}` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {residentForm.bed_id && (() => {
                              const bed = availableBeds.find((b) => b.id === residentForm.bed_id);
                              if (!bed) return null;
                              return (
                                <div className="mt-2 p-2 bg-primary/5 border border-primary/20 rounded text-xs text-muted-foreground">
                                  السرير المختار: <span className="text-primary font-medium">{bed.bed_number}</span>
                                  {' • '}السعر: <span className="text-primary">{bed.price} د.إ</span>
                                  {bed.locker_number && (<> {' • '}الخزانة: <span className="text-primary">{bed.locker_number}</span></>)}
                                </div>
                              );
                            })()}

                            {/* Inline quick-add bed (admin only) */}
                            {isAdmin() && housingRooms.length > 0 && (
                              <div className="mt-3 border border-amber-500/30 rounded-lg overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => setShowInlineBed((v) => !v)}
                                  className="w-full flex items-center justify-between px-3 py-2 bg-amber-500/10 hover:bg-amber-500/15 text-amber-300 text-xs"
                                  data-testid="toggle-inline-add-bed"
                                >
                                  <span className="flex items-center gap-1.5">
                                    <Plus className="w-3.5 h-3.5" />
                                    أضف سريراً جديداً من هنا (رقم + سعر + خزانة)
                                  </span>
                                  <span>{showInlineBed ? '−' : '+'}</span>
                                </button>
                                {showInlineBed && (
                                  <div className="p-3 bg-amber-500/5 space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className="block text-[10px] text-muted-foreground mb-1">الغرفة *</label>
                                        <Select
                                          value={inlineBed.room_id || undefined}
                                          onValueChange={(v) => setInlineBed({ ...inlineBed, room_id: v })}
                                        >
                                          <SelectTrigger className="h-8 bg-secondary/50 text-sm" data-testid="inline-bed-room">
                                            <SelectValue placeholder="اختر الغرفة" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {housingRooms.map((r) => (
                                              <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <label className="block text-[10px] text-muted-foreground mb-1">النوع *</label>
                                        <Select
                                          value={inlineBed.bed_type}
                                          onValueChange={(v) => setInlineBed({ ...inlineBed, bed_type: v })}
                                        >
                                          <SelectTrigger className="h-8 bg-secondary/50 text-sm" data-testid="inline-bed-type">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="lower">سفلي</SelectItem>
                                            <SelectItem value="upper">علوي</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <label className="block text-[10px] text-muted-foreground mb-1">رقم السرير *</label>
                                        <Input
                                          placeholder="A1"
                                          value={inlineBed.bed_number}
                                          onChange={(e) => setInlineBed({ ...inlineBed, bed_number: e.target.value })}
                                          className="h-8 bg-secondary/50 text-sm"
                                          data-testid="inline-bed-number"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] text-muted-foreground mb-1">السعر (د.إ) *</label>
                                        <Input
                                          type="number"
                                          placeholder="500"
                                          value={inlineBed.price}
                                          onChange={(e) => setInlineBed({ ...inlineBed, price: e.target.value })}
                                          className="h-8 bg-secondary/50 text-sm"
                                          data-testid="inline-bed-price"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] text-muted-foreground mb-1">الخزانة *</label>
                                        <Input
                                          placeholder="L1"
                                          value={inlineBed.locker_number}
                                          onChange={(e) => setInlineBed({ ...inlineBed, locker_number: e.target.value })}
                                          className="h-8 bg-secondary/50 text-sm"
                                          data-testid="inline-bed-locker"
                                        />
                                      </div>
                                    </div>
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={handleInlineAddBed}
                                      className="w-full h-8 bg-amber-500 hover:bg-amber-600 text-black text-xs"
                                      data-testid="inline-add-bed-submit"
                                    >
                                      <Plus className="w-3.5 h-3.5 me-1" />
                                      أضف السرير الآن وحدّده
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* معلومات مهمة */}
                          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
                            <p className="text-blue-400 font-medium">معلومات السكن:</p>
                            <ul className="text-muted-foreground text-xs mt-1 space-y-1 list-disc list-inside">
                              <li>مدة السكن: شهر كامل من تاريخ البداية</li>
                              <li>الإيجار غير مسترجع في حال الخروج المبكر</li>
                              <li>التكلفة: 500 درهم للحلاقين، 650 درهم للآخرين</li>
                            </ul>
                          </div>
                          
                          {/* صور الهوية (اختيارية) */}
                          <div className="border border-white/10 rounded-lg p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium flex items-center gap-2">
                                <Camera className="w-4 h-4 text-primary" />
                                صور الهوية <span className="text-muted-foreground text-xs">(اختيارية)</span>
                              </label>
                              {isAdmin() && (
                                <span className="text-xs text-green-400">يمكن للمدير تجاوز هذا الحقل</span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-muted-foreground mb-1">الوجه الأمامي</label>
                                <div className="relative">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        setResidentForm({ ...residentForm, id_front_file: file, id_front_preview: URL.createObjectURL(file) });
                                      }
                                    }}
                                    className="hidden"
                                    id="id-front-upload"
                                  />
                                  <label
                                    htmlFor="id-front-upload"
                                    className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
                                  >
                                    {residentForm.id_front_preview ? (
                                      <img src={residentForm.id_front_preview} alt="Front ID" className="h-full w-full object-cover rounded-lg" />
                                    ) : (
                                      <>
                                        <Upload className="w-6 h-6 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground mt-1">اضغط للتصوير</span>
                                      </>
                                    )}
                                  </label>
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs text-muted-foreground mb-1">الوجه الخلفي</label>
                                <div className="relative">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        setResidentForm({ ...residentForm, id_back_file: file, id_back_preview: URL.createObjectURL(file) });
                                      }
                                    }}
                                    className="hidden"
                                    id="id-back-upload"
                                  />
                                  <label
                                    htmlFor="id-back-upload"
                                    className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
                                  >
                                    {residentForm.id_back_preview ? (
                                      <img src={residentForm.id_back_preview} alt="Back ID" className="h-full w-full object-cover rounded-lg" />
                                    ) : (
                                      <>
                                        <Upload className="w-6 h-6 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground mt-1">اضغط للتصوير</span>
                                      </>
                                    )}
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* خيار الإقامة الدائمة - للمدير فقط */}
                          {isAdmin() && (
                            <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                              <input
                                type="checkbox"
                                id="permanent_stay"
                                checked={residentForm.permanent_stay}
                                onChange={(e) => {
                                  const isPermanent = e.target.checked;
                                  setResidentForm({ 
                                    ...residentForm, 
                                    permanent_stay: isPermanent,
                                    // إذا تم تفعيل الإقامة الدائمة، نزيل تاريخ الانتهاء وعدد الأيام
                                    end_date: isPermanent ? '' : residentForm.end_date,
                                    days_count: isPermanent ? '' : residentForm.days_count
                                  });
                                }}
                                className="w-5 h-5 accent-green-500"
                              />
                              <label htmlFor="permanent_stay" className="cursor-pointer">
                                <span className="font-medium text-green-400">إقامة دائمة</span>
                                <p className="text-xs text-muted-foreground">لن يتم إرسال تنبيهات انتهاء الإقامة لهذا الساكن</p>
                              </label>
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium mb-2">ملاحظات</label>
                            <Input
                              value={residentForm.notes}
                              onChange={(e) => setResidentForm({ ...residentForm, notes: e.target.value })}
                              className="bg-secondary/50 border-white/10"
                            />
                          </div>
                          <Button
                            type="submit"
                            disabled={!residentForm.bed_id}
                            className="w-full bg-primary text-primary-foreground disabled:opacity-50"
                            data-testid="submit-add-resident-btn"
                          >
                            {!residentForm.bed_id
                              ? 'اختر سريراً أولاً'
                              : (canEdit ? 'إضافة الساكن' : 'إرسال للموافقة')}
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>

                    {canEdit && (
                      <Button
                        onClick={() => setShowViewers(true)}
                        variant="outline"
                        size="sm"
                        className="border-white/10"
                      >
                        <Eye className="w-4 h-4 me-1" />
                        المشاهدون
                      </Button>
                    )}

                    {canEdit && (
                      <Button
                        onClick={() => setShowBlockedBarbers(true)}
                        variant="outline"
                        size="sm"
                        className="border-white/10 text-red-400 hover:text-red-300"
                      >
                        <Ban className="w-4 h-4 me-1" />
                        الحلاقين المحجوبين
                        {blockedBarbers.length > 0 && (
                          <Badge className="ms-2 bg-red-500">{blockedBarbers.length}</Badge>
                        )}
                      </Button>
                    )}

                    {canEdit && (
                      <Button
                        onClick={() => setShowAuthorizedBarbers(true)}
                        variant="outline"
                        size="sm"
                        className="border-white/10 text-green-400 hover:text-green-300"
                      >
                        <UserCheck className="w-4 h-4 me-1" />
                        المصرح لهم بالإضافة
                        {authorizedBarbers.length > 0 && (
                          <Badge className="ms-2 bg-green-500">{authorizedBarbers.length}</Badge>
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Residents Table */}
                  <div className="bg-card border border-white/5 rounded-xl p-4 overflow-x-auto">
                    <table className="w-full min-w-[1200px]">
                      <thead className="bg-secondary/50 border-b border-white/5">
                        <tr>
                          <th className="px-3 py-2 text-start text-sm font-semibold">الاسم</th>
                          <th className="px-3 py-2 text-start text-sm font-semibold">الهاتف</th>
                          <th className="px-3 py-2 text-start text-sm font-semibold">تاريخ البداية</th>
                          <th className="px-3 py-2 text-start text-sm font-semibold">تاريخ الانتهاء</th>
                          <th className="px-3 py-2 text-start text-sm font-semibold">الإيجار</th>
                          <th className="px-3 py-2 text-start text-sm font-semibold">الحالة</th>
                          <th className="px-3 py-2 text-start text-sm font-semibold">مسجل بواسطة</th>
                          <th className="px-3 py-2 text-start text-sm font-semibold">الإجراءات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeResidents.map((resident) => {
                          const discountInfo = resident.discount_info;
                          const hasLegacyDiscount = discountInfo?.eligible;
                          const monthDiscount = Number(resident.current_month_discount || 0);
                          const hasMonthlyDiscount = monthDiscount > 0;
                          const effectiveRent = resident.custom_rent ?? resident.stay_cost;
                          const finalRent = hasMonthlyDiscount
                            ? Math.max(0, effectiveRent - monthDiscount)
                            : (hasLegacyDiscount ? effectiveRent * 0.5 : effectiveRent);
                          const prorated = resident.prorated_info;
                          const approvalStatus = resident.approval_status || resident.status || 'approved';
                          const eligibleForSimpleDiscount = isAdmin() && !resident.is_system_user;
                          
                          return (
                            <tr key={resident.id} className={`border-b border-white/5 hover:bg-secondary/20 ${approvalStatus === 'pending' ? 'bg-yellow-500/5' : ''}`}>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  {resident.is_system_user && (
                                    <Badge variant="outline" className="text-xs bg-blue-500/20 text-blue-400 border-blue-500/30">
                                      حلاق
                                    </Badge>
                                  )}
                                  <span className="font-medium">{resident.name}</span>
                                  {resident.id_front_photo && (
                                    <span className="text-green-400 text-xs" title="الهوية مرفقة">📄</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">{resident.phone || '-'}</td>
                              <td className="px-3 py-2 text-muted-foreground">{resident.start_date}</td>
                              <td className="px-3 py-2">
                                {resident.permanent_stay ? (
                                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                                    ♾️ دائمة
                                  </Badge>
                                ) : resident.end_date ? (
                                  <span className="text-orange-400">{resident.end_date}</span>
                                ) : (
                                  <span className="text-muted-foreground text-xs">-</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {hasMonthlyDiscount ? (
                                  <div className="flex flex-col">
                                    <span className="text-muted-foreground line-through text-xs">
                                      <CurrencyDisplay amount={effectiveRent} />
                                    </span>
                                    <span className="text-emerald-400 font-bold">
                                      <CurrencyDisplay amount={finalRent} />
                                    </span>
                                    <span className="text-[10px] text-emerald-300/80">
                                      خصم {monthDiscount} د.إ ({resident.current_month})
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-primary font-bold">
                                    <CurrencyDisplay amount={effectiveRent} />
                                  </span>
                                )}
                                {resident.non_refundable && (
                                  <span className="text-xs text-red-400 block">غير مسترجع</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {approvalStatus === 'pending' ? (
                                  <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
                                    بانتظار الاعتماد
                                  </Badge>
                                ) : approvalStatus === 'approved' ? (
                                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                                    معتمد ✓
                                  </Badge>
                                ) : (
                                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                                    مرفوض
                                  </Badge>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-muted-foreground">
                                {resident.registered_by_name || resident.created_by_name || '-'}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  {/* زر الموافقة للمدير */}
                                  {isAdmin() && approvalStatus === 'pending' && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleApproveResident(resident.id, true)}
                                        className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                        title="اعتماد"
                                      >
                                        <CheckCircle className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleApproveResident(resident.id, false)}
                                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                        title="رفض"
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                  
                                  {/* زر التعديل */}
                                  {canEdit && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setEditingResident(resident)}
                                      className="text-primary hover:text-primary/80"
                                      title="تعديل"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                  
                                  {/* زر الحذف */}
                                  {canEdit && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteResident(resident.id)}
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                      title="حذف"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}

                                  {/* Phase 5: زر التجديد */}
                                  {canEdit && approvalStatus === 'approved' && !resident.permanent_stay && resident.renewal_status !== 'pending' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleRenewResident(resident.id, resident.name)}
                                      className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                                      title={isAdmin() ? 'تجديد شهر إضافي' : 'طلب تجديد شهر إضافي'}
                                      data-testid={`renew-resident-${resident.id}`}
                                    >
                                      <RefreshCw className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {resident.renewal_status === 'pending' && (
                                    <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-xs">
                                      تجديد بانتظار الاعتماد
                                    </Badge>
                                  )}
                                  
                                  {/* زر طلب خصم - للحلاقين فقط */}
                                  {!isAdmin() && !resident.discount_applied && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleRequestDiscount(resident)}
                                      className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                      title="طلب خصم"
                                    >
                                      <Percent className="w-4 h-4" />
                                    </Button>
                                  )}

                                  {/* زر تطبيق/إلغاء خصم 100 درهم — للمدير وللساكنين غير الحلاقين */}
                                  {eligibleForSimpleDiscount && !hasMonthlyDiscount && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleApplySimpleDiscount(selectedHousing.id, resident)}
                                      className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                      title="تطبيق خصم 100 د.إ لشهر واحد"
                                      data-testid={`apply-100-discount-${resident.id}`}
                                    >
                                      <Percent className="w-4 h-4" />
                                      <span className="text-[10px] ms-1">100</span>
                                    </Button>
                                  )}
                                  {eligibleForSimpleDiscount && hasMonthlyDiscount && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleRemoveSimpleDiscount(selectedHousing.id, resident)}
                                      className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                                      title={`إلغاء خصم شهر ${resident.current_month}`}
                                      data-testid={`remove-100-discount-${resident.id}`}
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  )}
                                  
                                  {/* عرض الخصم المطبق */}
                                  {resident.discount_applied && (
                                    <Badge className="bg-green-500/20 text-green-400 text-xs">
                                      خصم {resident.discount_percentage}%
                                    </Badge>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* عرض الساكنين الذين بانتظار الاعتماد */}
                  {pendingResidents.length > 0 && isAdmin() && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                      <h4 className="text-yellow-400 font-medium mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        ساكنين بانتظار الاعتماد ({pendingResidents.length})
                      </h4>
                      <div className="space-y-2">
                        {pendingResidents.map(pending => (
                          <div key={pending.resident?.id} className="flex items-center justify-between p-3 bg-card rounded-lg">
                            <div>
                              <span className="font-medium">{pending.resident?.name}</span>
                              <span className="text-sm text-muted-foreground ms-2">
                                مسجل بواسطة: {pending.resident?.registered_by_name || pending.resident?.created_by_name}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleApproveResident(pending.resident?.id, true)}
                                className="bg-green-500 hover:bg-green-600"
                              >
                                <CheckCircle className="w-4 h-4 me-1" />
                                اعتماد
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleApproveResident(pending.resident?.id, false)}
                              >
                                <XCircle className="w-4 h-4 me-1" />
                                رفض
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Phase 5: Pending renewal requests (admin) */}
                  {isAdmin() && pendingRenewals.length > 0 && (
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4" data-testid="pending-renewals-section">
                      <h4 className="text-cyan-400 font-medium mb-3 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" />
                        طلبات تجديد بانتظار الاعتماد ({pendingRenewals.length})
                      </h4>
                      <div className="space-y-2">
                        {pendingRenewals.map((req) => (
                          <div key={`${req.housing_id}-${req.resident_id}`} className="flex items-center justify-between p-3 bg-card rounded-lg">
                            <div className="flex-1">
                              <div className="font-medium">{req.resident_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {req.housing_name} · طلب بواسطة: {req.requested_by || '—'}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                من {req.current_end_date || '—'} إلى <span className="text-primary">{req.requested_new_end_date}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleApproveRenewal(req.housing_id, req.resident_id, true)}
                                className="bg-green-500 hover:bg-green-600"
                                data-testid={`approve-renewal-${req.resident_id}`}
                              >
                                <CheckCircle className="w-4 h-4 me-1" />
                                اعتماد
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleApproveRenewal(req.housing_id, req.resident_id, false)}
                                data-testid={`reject-renewal-${req.resident_id}`}
                              >
                                <XCircle className="w-4 h-4 me-1" />
                                رفض
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Stats Tab */}
                <TabsContent value="stats" className="space-y-4">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-card border border-white/5 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <DirhamIconColored className="w-4 h-4 text-primary" />
                        <span className="text-sm">الإيجار الشهري</span>
                      </div>
                      <div className="text-2xl font-bold text-primary">
                        <CurrencyDisplay amount={monthlyRent} className="text-primary" decimals={0} />
                      </div>
                    </div>
                    <div className="bg-card border border-white/5 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Users className="w-4 h-4" />
                        <span className="text-sm">عدد السكان</span>
                      </div>
                      <div className="text-2xl font-bold">{activeResidents.length}</div>
                    </div>
                    {profitability?.current_month && (
                      <>
                        <div className="bg-card border border-white/5 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <TrendingUp className="w-4 h-4" />
                            <span className="text-sm">إيراد الشهر</span>
                          </div>
                          <div className="text-2xl font-bold text-green-400">
                            <CurrencyDisplay amount={profitability.current_month.revenue || 0} className="text-green-400" decimals={0} />
                          </div>
                        </div>
                        <div className="bg-card border border-white/5 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            {profitability.current_month.is_profitable ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400" />
                            )}
                            <span className="text-sm">الربح الشهري</span>
                          </div>
                          <div className={`text-2xl font-bold ${profitability.current_month.is_profitable ? 'text-green-400' : 'text-red-400'}`}>
                            <CurrencyDisplay amount={profitability.current_month.profit || 0} decimals={0} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Expenses Summary */}
                  <div className="bg-card border border-white/5 rounded-xl p-6">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-red-400" />
                      ملخص المصاريف الشهرية
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-3 bg-secondary/30 rounded-lg">
                        <DirhamIconColored className="w-5 h-5 mx-auto text-primary mb-1" />
                        <div className="text-sm text-muted-foreground">الإيجار</div>
                        <div className="font-bold">{monthlyRent.toFixed(0)}</div>
                      </div>
                      <div className="text-center p-3 bg-secondary/30 rounded-lg">
                        <Home className="w-5 h-5 mx-auto text-blue-400 mb-1" />
                        <div className="text-sm text-muted-foreground">الأثاث/شهر</div>
                        <div className="font-bold">{(selectedHousing.furniture_cost / 12).toFixed(0)}</div>
                      </div>
                      <div className="text-center p-3 bg-secondary/30 rounded-lg">
                        <Zap className="w-5 h-5 mx-auto text-yellow-400 mb-1" />
                        <div className="text-sm text-muted-foreground">الكهرباء</div>
                        <div className="font-bold">{selectedHousing.electricity_bill}</div>
                      </div>
                      <div className="text-center p-3 bg-secondary/30 rounded-lg">
                        <Droplet className="w-5 h-5 mx-auto text-cyan-400 mb-1" />
                        <div className="text-sm text-muted-foreground">الماء</div>
                        <div className="font-bold">{selectedHousing.water_bill}</div>
                      </div>
                      <div className="text-center p-3 bg-secondary/30 rounded-lg">
                        <Wifi className="w-5 h-5 mx-auto text-green-400 mb-1" />
                        <div className="text-sm text-muted-foreground">الإنترنت</div>
                        <div className="font-bold">{selectedHousing.internet_bill}</div>
                      </div>
                    </div>
                    {profitability?.monthly_expenses && (
                      <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
                        <span className="text-sm text-muted-foreground">إجمالي المصاريف الشهرية: </span>
                        <span className="font-bold text-red-400">
                          <CurrencyDisplay amount={profitability.monthly_expenses.total || 0} className="text-red-400" decimals={0} />
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Profitability Summary */}
                  {profitability?.current_month && (
                    <div className={`rounded-xl p-6 ${
                      profitability.current_month.is_profitable 
                        ? 'bg-green-500/10 border border-green-500/20' 
                        : 'bg-red-500/10 border border-red-500/20'
                    }`}>
                      <div className="text-center">
                        <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                          profitability.current_month.is_profitable 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {profitability.current_month.is_profitable ? 'السكن مربح' : 'السكن غير مربح'}
                        </span>
                        {profitability.can_add_new_housing && (
                          <p className="text-green-400 text-sm mt-2">
                            يمكن إضافة سكن جديد ({profitability.profitable_months_count} شهور مربحة)
                          </p>
                        )}
                      </div>

                      {/* Discount Info for Admin */}
                      {canEdit && profitability.residents_with_discount?.length > 0 && (
                        <div className="mt-4 p-4 bg-green-500/10 rounded-lg">
                          <h4 className="font-bold text-green-400 mb-2 flex items-center gap-2">
                            <Award className="w-4 h-4" />
                            ساكنون مؤهلون للخصم (إيراد 5000+ درهم)
                          </h4>
                          <div className="space-y-2">
                            {profitability.residents_with_discount.map((r) => (
                              <div key={r.user_id || r.name} className="flex justify-between items-center text-sm">
                                <span>{r.name}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-muted-foreground">إيراد: {r.revenue} درهم</span>
                                  <Badge className="bg-green-500 text-white">
                                    خصم {r.discount_amount} درهم
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* Pending Tab (Admin Only) */}
                {canEdit && (
                  <TabsContent value="pending" className="space-y-4">
                    {/* Pending Residents */}
                    <div className="bg-card border border-white/5 rounded-xl p-4">
                      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-yellow-400" />
                        ساكنون بانتظار الموافقة
                      </h3>
                      {pendingResidents.length === 0 ? (
                        <p className="text-center text-muted-foreground py-4">لا يوجد طلبات معلقة</p>
                      ) : (
                        <div className="space-y-3">
                          {pendingResidents.map(resident => (
                            <div key={resident.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                              <div>
                                <div className="font-medium">{resident.name}</div>
                                <div className="text-sm text-muted-foreground">
                                  أضافه: {resident.created_by_name} | {resident.start_date}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleApproveResident(resident.id, true)}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="w-4 h-4 me-1" />
                                  موافقة
                                </Button>
                                <Button
                                  onClick={() => handleApproveResident(resident.id, false)}
                                  size="sm"
                                  variant="destructive"
                                >
                                  <XCircle className="w-4 h-4 me-1" />
                                  رفض
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Pending Evacuations */}
                    <div className="bg-card border border-white/5 rounded-xl p-4">
                      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                        <LogOut className="w-5 h-5 text-orange-400" />
                        طلبات الإخلاء
                      </h3>
                      {pendingEvacuations.length === 0 ? (
                        <p className="text-center text-muted-foreground py-4">لا يوجد طلبات إخلاء</p>
                      ) : (
                        <div className="space-y-3">
                          {pendingEvacuations.map(req => (
                            <div key={req.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                              <div>
                                <div className="font-medium">{req.resident_name}</div>
                                <div className="text-sm text-muted-foreground">
                                  تاريخ الإخلاء: {req.evacuation_date} | طلب من: {req.created_by_name}
                                </div>
                                {req.reason && <div className="text-sm text-yellow-400">السبب: {req.reason}</div>}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleApproveEvacuation(req.id, true)}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  موافقة
                                </Button>
                                <Button
                                  onClick={() => handleApproveEvacuation(req.id, false)}
                                  size="sm"
                                  variant="destructive"
                                >
                                  رفض
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Pending Deletions */}
                    <div className="bg-card border border-white/5 rounded-xl p-4">
                      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                        <Trash2 className="w-5 h-5 text-red-400" />
                        طلبات الحذف
                      </h3>
                      {pendingDeletions.length === 0 ? (
                        <p className="text-center text-muted-foreground py-4">لا يوجد طلبات حذف</p>
                      ) : (
                        <div className="space-y-3">
                          {pendingDeletions.map(req => (
                            <div key={req.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                              <div>
                                <div className="font-medium">{req.resident_name}</div>
                                <div className="text-sm text-muted-foreground">
                                  طلب من: {req.created_by_name} | {new Date(req.created_at).toLocaleDateString('ar-AE')}
                                </div>
                                {req.reason && <div className="text-sm text-yellow-400">السبب: {req.reason}</div>}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleApproveDeletion(req.id, true)}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  موافقة
                                </Button>
                                <Button
                                  onClick={() => handleApproveDeletion(req.id, false)}
                                  size="sm"
                                  variant="destructive"
                                >
                                  رفض
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Pending Discount Requests */}
                    <div className="bg-card border border-white/5 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-lg flex items-center gap-2">
                          <Percent className="w-5 h-5 text-green-400" />
                          طلبات الخصم (50%)
                        </h3>
                        {selectedHousing && (
                          <Button
                            onClick={() => checkDiscountEligibility(selectedHousing.id)}
                            size="sm"
                            variant="outline"
                            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                          >
                            <RefreshCw className="w-4 h-4 me-1" />
                            فحص الأهلية
                          </Button>
                        )}
                      </div>
                      
                      {/* Discount Stats */}
                      {discountStats && (
                        <div className="grid grid-cols-4 gap-2 mb-4">
                          <div className="bg-yellow-500/10 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-yellow-400">{discountStats.pending}</div>
                            <div className="text-xs text-muted-foreground">معلق</div>
                          </div>
                          <div className="bg-green-500/10 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-green-400">{discountStats.approved}</div>
                            <div className="text-xs text-muted-foreground">موافق</div>
                          </div>
                          <div className="bg-red-500/10 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-red-400">{discountStats.rejected}</div>
                            <div className="text-xs text-muted-foreground">مرفوض</div>
                          </div>
                          <div className="bg-primary/10 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-primary">{discountStats.total_discount_saved}</div>
                            <div className="text-xs text-muted-foreground">إجمالي التوفير</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Conditions Note */}
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4 text-sm">
                        <p className="text-blue-400">
                          <strong>شروط الخصم:</strong> إيراد الحلاق ≥ 5000 درهم + عدد السكان ≥ 8
                        </p>
                      </div>
                      
                      {pendingDiscounts.length === 0 ? (
                        <p className="text-center text-muted-foreground py-4">لا يوجد طلبات خصم معلقة</p>
                      ) : (
                        <div className="space-y-3">
                          {pendingDiscounts.map(req => (
                            <div key={req.id} className="p-4 bg-secondary/30 rounded-lg border border-green-500/20">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-bold text-lg">{req.resident_name}</span>
                                    <Badge className="bg-green-500/20 text-green-400">
                                      <Award className="w-3 h-3 me-1" />
                                      مؤهل للخصم
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    <div>
                                      <span className="text-muted-foreground">الشهر:</span>
                                      <span className="ms-1 font-medium">{req.month}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">الإيراد:</span>
                                      <span className="ms-1 font-medium text-green-400">{req.revenue} درهم</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">الإيجار الأصلي:</span>
                                      <span className="ms-1 font-medium">{req.original_rent} درهم</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">عدد السكان:</span>
                                      <span className="ms-1 font-medium">{req.residents_count}</span>
                                    </div>
                                  </div>
                                  <div className="mt-2 p-2 bg-primary/10 rounded-lg">
                                    <span className="text-muted-foreground">الخصم المطلوب:</span>
                                    <span className="ms-2 font-bold text-primary text-lg">{req.requested_discount_percentage}%</span>
                                    <span className="text-muted-foreground ms-2">= </span>
                                    <span className="font-bold text-green-400">{(req.original_rent * req.requested_discount_percentage / 100).toFixed(0)} درهم</span>
                                    <span className="text-muted-foreground ms-2">← الإيجار الجديد: </span>
                                    <span className="font-bold text-primary">{(req.original_rent * (100 - req.requested_discount_percentage) / 100).toFixed(0)} درهم</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="flex gap-2 mt-4 flex-wrap">
                                <Button
                                  onClick={() => openDiscountModal(req, 'approve')}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="w-4 h-4 me-1" />
                                  موافقة (50%)
                                </Button>
                                <Button
                                  onClick={() => openDiscountModal(req, 'modify')}
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  <Percent className="w-4 h-4 me-1" />
                                  تعديل النسبة
                                </Button>
                                <Button
                                  onClick={() => openDiscountModal(req, 'reject')}
                                  size="sm"
                                  variant="destructive"
                                >
                                  <XCircle className="w-4 h-4 me-1" />
                                  رفض
                                </Button>
                                <Button
                                  onClick={() => openDiscountModal(req, 'postpone')}
                                  size="sm"
                                  variant="outline"
                                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                                >
                                  <PauseCircle className="w-4 h-4 me-1" />
                                  تأجيل
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                )}

                {/* Reports Tab (Admin Only) */}
                {canEdit && (
                  <TabsContent value="reports" className="space-y-4">
                    <div className="bg-card border border-white/5 rounded-xl p-6">
                      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        إنشاء تقرير
                      </h3>
                      <form onSubmit={handleGenerateReport} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">نوع التقرير</label>
                          <Select 
                            value={reportForm.report_type} 
                            onValueChange={(v) => setReportForm({ ...reportForm, report_type: v })}
                          >
                            <SelectTrigger className="bg-secondary/50 border-white/10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-white/10">
                              <SelectItem value="monthly">شهري</SelectItem>
                              <SelectItem value="quarterly">ربع سنوي</SelectItem>
                              <SelectItem value="custom">مخصص</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {reportForm.report_type === 'monthly' && (
                          <div>
                            <label className="block text-sm font-medium mb-2">الشهر</label>
                            <Input
                              type="month"
                              value={reportForm.month}
                              onChange={(e) => setReportForm({ ...reportForm, month: e.target.value })}
                              className="bg-secondary/50 border-white/10"
                            />
                          </div>
                        )}

                        {reportForm.report_type === 'custom' && (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">من تاريخ</label>
                              <Input
                                type="date"
                                value={reportForm.start_date}
                                onChange={(e) => setReportForm({ ...reportForm, start_date: e.target.value })}
                                className="bg-secondary/50 border-white/10"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">إلى تاريخ</label>
                              <Input
                                type="date"
                                value={reportForm.end_date}
                                onChange={(e) => setReportForm({ ...reportForm, end_date: e.target.value })}
                                className="bg-secondary/50 border-white/10"
                              />
                            </div>
                          </div>
                        )}

                        <Button type="submit" className="bg-primary text-primary-foreground">
                          <FileText className="w-4 h-4 me-2" />
                          إنشاء التقرير
                        </Button>
                      </form>

                      {/* Report Results */}
                      {reportData && (
                        <div className="mt-6 p-4 bg-secondary/30 rounded-lg">
                          <h4 className="font-bold mb-4">تقرير: {reportData.housing_name}</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div className="text-center">
                              <div className="text-sm text-muted-foreground">الإيرادات</div>
                              <div className="text-xl font-bold text-green-400">
                                <CurrencyDisplay amount={reportData.total_revenue} />
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm text-muted-foreground">المصاريف</div>
                              <div className="text-xl font-bold text-red-400">
                                <CurrencyDisplay amount={reportData.total_expenses} />
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm text-muted-foreground">الخصومات</div>
                              <div className="text-xl font-bold text-yellow-400">
                                <CurrencyDisplay amount={reportData.total_discounts} />
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm text-muted-foreground">صافي الربح</div>
                              <div className={`text-xl font-bold ${reportData.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                <CurrencyDisplay amount={reportData.net_profit} />
                              </div>
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            الفترة: {reportData.start_date} إلى {reportData.end_date}
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </div>
          )}
        </div>
        </>
      )}

      {/* Edit Housing Dialog */}
      <HousingFormDialog
        open={!!editingHousing}
        onOpenChange={(open) => !open && setEditingHousing(null)}
        mode="edit"
        form={housingForm}
        setForm={setHousingForm}
        onSubmit={handleUpdateHousing}
      />

      {/* Rooms & Beds Manager */}
      <RoomsAndBedsManager
        open={!!roomsBedsHousing}
        onOpenChange={(o) => !o && setRoomsBedsHousing(null)}
        housing={roomsBedsHousing}
        onChanged={() => {
          if (roomsBedsHousing?.id) fetchAvailableBeds(roomsBedsHousing.id);
          fetchHousings();
        }}
      />

      {/* Viewers Dialog */}
      <ViewersDialog
        open={showViewers}
        onOpenChange={setShowViewers}
        selectedHousing={selectedHousing}
        allUsers={allUsers}
        onRemoveViewer={handleRemoveViewer}
        onAddViewer={handleAddViewer}
      />

      {/* Evacuation Dialog */}
      <EvacuationDialog
        open={showEvacuation}
        onOpenChange={setShowEvacuation}
        form={evacuationForm}
        setForm={setEvacuationForm}
        onSubmit={handleCreateEvacuation}
      />

      {/* Blocked Barbers Dialog */}
      <BlockedBarbersDialog
        open={showBlockedBarbers}
        onOpenChange={setShowBlockedBarbers}
        blockedBarbers={blockedBarbers}
        allUsers={allUsers}
        onUnblock={handleUnblockBarber}
        onBlock={handleBlockBarber}
      />

      {/* Authorized Barbers Dialog */}
      <AuthorizedBarbersDialog
        open={showAuthorizedBarbers}
        onOpenChange={setShowAuthorizedBarbers}
        authorizedBarbers={authorizedBarbers}
        allUsers={allUsers}
        onRevoke={handleRevokeAuthorization}
        onAuthorize={handleAuthorizeBarber}
      />

      {/* Discount Request Action Modal */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {discountAction === 'approve' && <CheckCircle className="w-5 h-5 text-green-500" />}
              {discountAction === 'reject' && <XCircle className="w-5 h-5 text-red-500" />}
              {discountAction === 'modify' && <Percent className="w-5 h-5 text-blue-500" />}
              {discountAction === 'postpone' && <PauseCircle className="w-5 h-5 text-orange-500" />}
              {discountAction === 'approve' && 'موافقة على طلب الخصم'}
              {discountAction === 'reject' && 'رفض طلب الخصم'}
              {discountAction === 'modify' && 'تعديل نسبة الخصم'}
              {discountAction === 'postpone' && 'تأجيل القرار'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDiscountRequest && (
            <div className="space-y-4">
              {/* Request Info */}
              <div className="bg-secondary/30 rounded-lg p-3">
                <p className="font-medium mb-2">{selectedDiscountRequest.resident_name}</p>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>الشهر: {selectedDiscountRequest.month}</p>
                  <p>الإيراد: <span className="text-green-400">{selectedDiscountRequest.revenue} درهم</span></p>
                  <p>الإيجار الأصلي: {selectedDiscountRequest.original_rent} درهم</p>
                </div>
              </div>
              
              {/* Approve Action */}
              {discountAction === 'approve' && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-400">
                    سيتم تطبيق خصم <strong>50%</strong> على الإيجار
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    الإيجار الجديد: <strong>{(selectedDiscountRequest.original_rent * 0.5).toFixed(0)} درهم</strong>
                  </p>
                </div>
              )}
              
              {/* Modify Action */}
              {discountAction === 'modify' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">نسبة الخصم المخصصة (%)</label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={customDiscountPercent}
                      onChange={(e) => setCustomDiscountPercent(parseFloat(e.target.value) || 0)}
                      className="bg-secondary/50 border-white/10"
                    />
                  </div>
                  <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <p className="text-blue-400">
                      الخصم: <strong>{customDiscountPercent}%</strong> = {(selectedDiscountRequest.original_rent * customDiscountPercent / 100).toFixed(0)} درهم
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      الإيجار الجديد: <strong>{(selectedDiscountRequest.original_rent * (100 - customDiscountPercent) / 100).toFixed(0)} درهم</strong>
                    </p>
                  </div>
                </div>
              )}
              
              {/* Reject Action */}
              {discountAction === 'reject' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">سبب الرفض *</label>
                    <Textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="اكتب سبب رفض طلب الخصم..."
                      className="bg-secondary/50 border-white/10 min-h-[100px]"
                      required
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    سيتم إرسال سبب الرفض للحلاق عبر البريد الإلكتروني
                  </p>
                </div>
              )}
              
              {/* Postpone Action */}
              {discountAction === 'postpone' && (
                <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <p className="text-orange-400">
                    سيتم تأجيل القرار على هذا الطلب. يمكنك العودة إليه لاحقاً.
                  </p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDiscountModal(false)}
                  className="border-white/10"
                >
                  إلغاء
                </Button>
                <Button
                  onClick={handleDiscountAction}
                  className={
                    discountAction === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                    discountAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                    discountAction === 'modify' ? 'bg-blue-600 hover:bg-blue-700' :
                    'bg-orange-600 hover:bg-orange-700'
                  }
                >
                  {discountAction === 'approve' && 'تأكيد الموافقة'}
                  {discountAction === 'reject' && 'تأكيد الرفض'}
                  {discountAction === 'modify' && 'تطبيق النسبة'}
                  {discountAction === 'postpone' && 'تأكيد التأجيل'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* نافذة طلب الخصم - للحلاقين */}
      {discountRequest.show && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-card p-6 rounded-xl max-w-md w-full mx-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Percent className="w-5 h-5 text-green-400" />
                طلب خصم للساكن
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDiscountRequest({ show: false, resident: null, reason: '', loading: false, conditionsMessage: null })}
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            
            {/* معلومات الساكن */}
            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="font-medium">{discountRequest.resident?.name}</p>
              <p className="text-sm text-muted-foreground">
                الإيجار الحالي: <span className="text-primary font-bold">{discountRequest.resident?.stay_cost || 650} درهم</span>
              </p>
              {discountRequest.resident?.is_system_user && (
                <Badge className="mt-2 bg-blue-500/20 text-blue-400 text-xs">حلاق</Badge>
              )}
            </div>
            
            {/* شروط الخصم للحلاقين */}
            {discountRequest.conditionsMessage && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 font-medium text-sm mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  شروط الخصم للحلاقين:
                </p>
                <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-sans">
                  {discountRequest.conditionsMessage}
                </pre>
              </div>
            )}
            
            {/* سبب الطلب */}
            <div>
              <label className="block text-sm font-medium mb-2">سبب الطلب (اختياري)</label>
              <Input
                value={discountRequest.reason}
                onChange={(e) => setDiscountRequest(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="اكتب سبب طلب الخصم..."
                className="bg-secondary/50 border-white/10"
              />
            </div>
            
            {/* نسبة الخصم */}
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 font-medium">نسبة الخصم المطلوبة: 50%</p>
              <p className="text-xs text-muted-foreground">
                الإيجار بعد الخصم: <span className="font-bold">{((discountRequest.resident?.stay_cost || 650) * 0.5).toFixed(0)} درهم</span>
              </p>
            </div>
            
            {/* أزرار */}
            <div className="flex gap-3">
              <Button
                onClick={submitDiscountRequest}
                disabled={discountRequest.loading}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {discountRequest.loading ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <>
                    <Send className="w-4 h-4 me-2" />
                    إرسال الطلب
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setDiscountRequest({ show: false, resident: null, reason: '', loading: false, conditionsMessage: null })}
              >
                إلغاء
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeHousing;
